import * as React from "react";
import { cn } from "@/lib/utils";
import { X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input, InputGroup, InputHelperText } from "@/components/ui/input";
import { HintTooltip, type HintTooltipSection } from "./HintTooltip";
import { INPUT_FORMATS } from "./CustomForm";

/** 요금단가의 단위. 서버는 숫자만 받고 단위는 화면 표기다 */
const UNIT_PRICE_SUFFIX = "원/kWh";

export interface UnitPriceApplyFieldProps {
  /**
   * 라벨. 라벨 우측의 부가정보(충전소명, "총 16기 중 4기 선택")도 여기에 담는다 —
   * 라벨 영역은 flex row 라 `<span>` 으로 스타일을 따로 줄 수 있다.
   */
  label: React.ReactNode;
  /** 라벨 우측 ⓘ 호버 툴팁. 시안이 제목 + 본문 구조라 문자열이 아니라 섹션으로 받는다 */
  tooltip?: HintTooltipSection[];
  value: string;
  onChange: (value: string) => void;
  /** [적용] 클릭. 값이 비어 있거나 이미 적용된 값이면 버튼이 비활성이라 호출되지 않는다 */
  onApply: (value: string) => void;
  /**
   * 이미 적용된 단가. 입력값과 같으면 [적용] 을 비활성해 "반영됐다"를 드러낸다.
   * 미설정이면 null. 넘기지 않으면 값이 있는 동안 계속 활성이다(기존 동작).
   */
  appliedValue?: string | null;
  applyLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | null;
  /** 넘기면 라벨 우측에 해제 버튼이 노출된다 (조건부 노출은 호출부가 판단) */
  onClear?: () => void;
  clearLabel?: string;
  /**
   * 라벨 위치. `stacked`(기본)는 라벨을 입력 위에, `inline`은 라벨을 좌측에 둔다 —
   * 기본 요금단가처럼 모달 폭을 가로로 다 쓰는 한 줄짜리 자리에 쓴다.
   */
  layout?: "stacked" | "inline";
  className?: string;
}

/**
 * 단가 입력 + `원/kWh` 단위 + [적용] 버튼 한 줄.
 *
 * 입력만으로는 반영되지 않고 [적용] 을 눌러야 상위 상태에 들어간다 — 기본·충전소·충전기
 * 세 층이 같은 조작을 반복하므로 컴포넌트로 고정한다. 값은 문자열로 다룬다(서버가 소수
 * 문자열을 주고받아 number 로 왕복하면 정밀도가 깎인다).
 *
 * ref 는 내부 input 으로 전달된다 — 필수값 검증 실패 시 포커스를 옮기는 데 쓴다.
 *
 * @example
 * ```tsx
 * <UnitPriceApplyField
 *   label="기본 요금단가"
 *   required
 *   tooltip={[{ title: "기본 요금단가", body: "별도 단가가 없을 때 적용돼요." }]}
 *   value={basePriceInput}
 *   onChange={setBasePriceInput}
 *   onApply={(value) => applyBasePrice(value)}
 *   error={basePriceError}
 * />
 * ```
 */
export const UnitPriceApplyField = React.forwardRef<
  HTMLInputElement,
  UnitPriceApplyFieldProps
>(function UnitPriceApplyField(
  {
    label,
    tooltip,
    value,
    onChange,
    onApply,
    appliedValue,
    applyLabel = "적용",
    placeholder,
    disabled = false,
    required = false,
    error,
    onClear,
    clearLabel,
    layout = "stacked",
    className,
  },
  ref,
) {
  const trimmed = value.trim();
  // 문자열이 아니라 수치로 비교한다 — 서버가 "100.00" 을 주고 사용자가 "100" 을 치면 같은 단가다
  const isAlreadyApplied =
    trimmed.length > 0 &&
    appliedValue != null &&
    appliedValue.trim().length > 0 &&
    Number(appliedValue) === Number(trimmed);
  const canApply = !disabled && trimmed.length > 0 && !isAlreadyApplied;
  const isInline = layout === "inline";

  return (
    <div
      className={cn(
        isInline ? "flex items-start gap-4" : "flex flex-col gap-1.5",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-1.5",
          // 입력 박스(36px)에 라벨을 수직 중앙 정렬하고 라벨 열 폭을 고정한다
          isInline && "h-9 w-29 shrink-0",
        )}
      >
        {/* 필수 표시는 라벨 뒤 `*` — FormRow 등 다른 폼 라벨과 같은 규칙이다 */}
        <span className="typo-body-2-semibold flex items-center gap-0.5 text-labels-primary">
          {label}
          {required && "*"}
        </span>
        {tooltip && (
          <HintTooltip sections={tooltip} iconSize={isInline ? 20 : 16} />
        )}
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            // 밑줄 있는 13px semibold 링크 (시안) — 버튼 형태가 아니라 텍스트 링크다
            className="typo-label-2-semibold ml-auto inline-flex cursor-pointer items-center gap-0.5 border-b border-labels-assistive text-labels-alternative"
          >
            {clearLabel ?? "요금 해제"}
            <X size={16} color="labels-alternative" />
          </button>
        )}
      </div>

      {/* inline 에서도 에러 문구는 입력 아래에 붙어야 하므로 한 겹 더 감싼다 */}
      <div
        className={cn(
          "flex min-w-0 flex-col gap-1.5",
          isInline ? "flex-1" : "w-full",
        )}
      >
        {/* [적용] 은 입력 박스에서 12px 떨어진 별도 버튼이고, 값이 있으면 solid
            primary 로 채워진다 (시안) */}
        <div className="flex min-w-0 items-center gap-3">
          {/* 36px 높이·6px 라운드가 시안 규격이다 — inline 사이즈에 높이만 올려 맞춘다 */}
          <InputGroup
            size="inline"
            className="h-9 min-w-0 flex-1"
            error={!!error}
            disabled={disabled}
          >
            <Input
              ref={ref}
              value={value}
              inputMode="decimal"
              placeholder={placeholder}
              maxLength={INPUT_FORMATS.decimal.maxLength}
              // 숫자와 소수 2자리만 남긴다 — 공백·문자는 입력 시점에 걸러진다
              onChange={(event) =>
                onChange(INPUT_FORMATS.decimal.format(event.target.value))
              }
            />
            <InputHelperText>{UNIT_PRICE_SUFFIX}</InputHelperText>
          </InputGroup>

          <Button
            type="button"
            theme="normal"
            size="sm"
            className="rounded-md"
            disabled={!canApply}
            onClick={() => onApply(trimmed)}
          >
            {applyLabel}
          </Button>
        </div>

        {error && (
          <p className="typo-caption-1-medium text-accent-mute-red">{error}</p>
        )}
      </div>
    </div>
  );
});
